import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Keyboard, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";

import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import ToastMessage from "@components/Toast";

import t from "@constants/localization";
import color from "@constants/color";

import OTPInput from "./OTPInput";
import InstantCommissionModal from "./InstantCommissionModal";
import LinearGradient from "react-native-linear-gradient";
import LoadingModal from "@components/ModalLoader";
import { useDispatch, useSelector } from "react-redux";
import { getDeliveryOtpViaSms, markMultipleDeliveries } from "@services/api";
import useLocationHook from "@components/common/LocationHook";
import { actions as myTaskActions } from "@reducers/myTaskReducer";
import PaymentComponent from "./PaymentComponent";
import useCountDown from "react-countdown-hook";
import OtpNumberModal from "@screens/RRD/OtpNumberModal";
import Text from "@components/CustomText";

interface RouteParams {
  otpLength: number;
  otpText: string;
  online_payment_enabled: boolean;
  collectible_amount: string;
  cx_user_id: string;
  paymentMode: string;
  phone_numbers: string[];
  extra_info: string;
  status: string;
  amount: string;
  paytm_order_id: string;
}

interface Props {
  route: {
    params: RouteParams;
  };
}

interface ResendOtpHelperProps {
  phone_numbers: string[];
  setShowNumberModal: (show: boolean) => void;
  resendOtp: (phoneNumber: string) => Promise<void>;
}

interface PrimaryResendOTPLineProps {
  phone_numbers: string[];
  showResend: boolean;
  setShowNumberModal: (show: boolean) => void;
  resendOtp: (phoneNumber: string) => Promise<void>;
  selectedPhoneNumber: string;
}

interface SecondaryResendOTPLineProps {
  showResend: boolean;
  isCountDownRunning: boolean;
  countDown: number;
  setShowNumberModal: (show: boolean) => void;
  phone_numbers: string[];
  resendOtp: (phoneNumber: string) => Promise<void>;
}

const NewOTPVerification: React.FC<Props> = ({ route }) => {
  const params = route.params || {};
  const {
    otpLength,
    otpText,
    online_payment_enabled,
    collectible_amount,
    cx_user_id,
    paymentMode,
    phone_numbers,
    extra_info,
    status,
    amount,
    paytm_order_id,
  } = params;
  const navigation = useNavigation();
  const [otp, setOtp] = useState("");
  const [otpMsgText, setOtpMsgText] = useState(otpText || "");
  const [showInstantCommissionModal, setShowInstantCommissionModal] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [commissionMsgs, setCommissionMsgs] = useState({});
  const { orderMarkingMap } = useSelector((state) => state.myTaskReducer);
  const getCurrentLocation = useLocationHook();
  const [countDown, { start, reset }] = useCountDown(30 * 1000, 1000);
  const [showResend, setShowResend] = useState(false);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const resendOtpBtnLoadingRef = useRef(false);
  const dispatch = useDispatch();
  const [otpError, setOtpError] = useState(false);

  const handleVerification = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const coords = await getCurrentLocation(2);
      const payload = {
        orders: Object.values(orderMarkingMap),
        otp: `${otp}`,
        lat: coords.latitude,
        lng: coords.longitude,
        otp_enabled: true,
        online_payment_enabled: online_payment_enabled,
        collectible_amount: parseFloat(collectible_amount),
        cx_user_id: cx_user_id,
        status,
        amount,
        paytm_order_id,
        payment_mode: paymentMode,
      };

      const response = await markMultipleDeliveries(payload);

      if (response?.data?.status === 200) {
        dispatch(myTaskActions.clearOrderMarking());
        if (response?.data?.payload?.type === "INSTANT_COMMISSION_TRIGGERED") {
          setCommissionMsgs(response?.data?.payload);
          setShowInstantCommissionModal(true);
          return;
        }
        ToastMessage("success", response?.data?.payload?.toastMessage);
        navigation.pop(2);
      } else {
        setOtpError(true);
        setOtpMsgText(response?.data?.payload?.errorText);
      }
    } catch (error: any) {
      setOtpMsgText(error.message);
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };
  const isCountDownRunning = useMemo(() => {
    return countDown > 0;
  }, [countDown]);

  const resendOtp = async (phoneNumber: string): Promise<void> => {
    if (resendOtpBtnLoadingRef.current) return;
    resendOtpBtnLoadingRef.current = true;
    try {
      setSelectedPhoneNumber(phoneNumber);

      const response = await getDeliveryOtpViaSms(
        phoneNumber,
        Object.keys(orderMarkingMap),
        collectible_amount,
      );
      ToastMessage(
        "success",
        response?.data?.toastMessage || "OTP Sent Successfully",
        1500,
      );
      reset();
      start();
      setShowResend(true);
    } catch (err) {
      ToastMessage("error", "Try Again", 1500);
    } finally {
      resendOtpBtnLoadingRef.current = false;
      setShowNumberModal(false);
    }
  };

  const handleOtpChange = (value: string): void => {
    if (otpError) {
      setOtpError(false);
      setOtpMsgText(otpText || "");
    }
    setOtp(value);
  };

  function resendOtpHelper({
    phone_numbers,
    setShowNumberModal,
    resendOtp,
  }: ResendOtpHelperProps) {
    if (!phone_numbers?.length) return null;

    if (phone_numbers?.length > 1) {
      setShowNumberModal(true);
      return;
    }
    resendOtp(phone_numbers[0]);
  }

  function PrimaryResendOTPLine({
    phone_numbers,
    showResend,
    setShowNumberModal,
    resendOtp,
    selectedPhoneNumber,
  }: PrimaryResendOTPLineProps) {
    return !showResend ? (
      <Pressable
        style={styles.resendOtpContainer}
        onPress={() => {
          resendOtpHelper({
            phone_numbers,
            setShowNumberModal,
            resendOtp,
          });
        }}
        testID="resendOtpSingleNumber"
      >
        <Text
          newfont
          variant="body_text_4"
          style={styles.resendOtpText}
        >{`मैसेज के माध्यम से OTP भेजें `}</Text>
        <Text
          newfont
          variant="body_text_4"
          style={styles.phoneNumberText}
        >{`${phone_numbers?.length ? (phone_numbers?.length > 1 ? "" : `(फ़ोन : ${phone_numbers[0]})`) : ""}`}</Text>
      </Pressable>
    ) : (
      <View style={styles.centeredContainer}>
        <Text
          newfont
          variant="body_text_3"
        >{`OTP ${selectedPhoneNumber} पर भेजा गया`}</Text>
      </View>
    );
  }

  function SecondaryResendOTPLine({
    showResend,
    isCountDownRunning,
    countDown,
    setShowNumberModal,
    phone_numbers,
    resendOtp,
  }: SecondaryResendOTPLineProps) {
    return showResend ? (
      <View style={styles.rowCentered}>
        <Text newfont variant="body_text_3" style={styles.mediumBlackText}>
          OTP नहीं मिला?
        </Text>
        {isCountDownRunning ? (
          <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
            दोबारा भेजें
          </Text>
        ) : null}
        <Pressable
          onPress={() => {
            if (isCountDownRunning) return;
            resendOtpHelper({
              phone_numbers,
              setShowNumberModal,
              resendOtp,
            });
          }}
          testID="resendOtpSingleNumber"
        >
          <Text
            newfont
            variant="body_text_2"
            style={[
              styles.resendActionText,
              isCountDownRunning && styles.countdownText,
            ]}
          >
            {isCountDownRunning ? `${countDown / 1000} sec` : "दोबारा भेजें"}
          </Text>
        </Pressable>
        {isCountDownRunning ? (
          <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
            में
          </Text>
        ) : null}
      </View>
    ) : null;
  }

  useEffect(() => {
    if (otp && otp.length === otpLength) {
      Keyboard.dismiss();
      handleVerification();
    }
  }, [otp]);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t("screens/RRD/OTPVerification/header")}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <LinearGradient
        colors={["rgba(99, 99, 99, 1)", "rgba(18, 18, 18, 1)"]}
        start={{ x: 0.0, y: 0.1 }}
        end={{ x: 0.9, y: 0.4 }}
        style={styles.innerContainer}
      >
        <PaymentComponent
          collectible_amount={collectible_amount}
          paymentMode={paymentMode}
        />
        <OTPInput
          value={otp}
          valueLength={otpLength}
          onChange={handleOtpChange}
          otpText={otpMsgText}
          error={otpError}
        />
      </LinearGradient>
      {extra_info ? (
        <Text newfont variant="body_text_3" style={styles.extraInfoText}>
          {extra_info}
        </Text>
      ) : null}
      <PrimaryResendOTPLine
        {...{
          phone_numbers,
          showResend,
          resendOtp,
          selectedPhoneNumber,
          setShowNumberModal,
        }}
      />
      <SecondaryResendOTPLine
        {...{
          showResend,
          isCountDownRunning,
          countDown,
          setShowNumberModal,
          resendOtp,
          phone_numbers,
        }}
      />
      {showInstantCommissionModal && (
        <InstantCommissionModal
          isVisible={showInstantCommissionModal}
          onClose={() => {
            navigation.pop(2);
          }}
          commissionMsgs={commissionMsgs}
        />
      )}
      <LoadingModal loading={isLoading} />
      <OtpNumberModal
        visible={showNumberModal}
        onPress={resendOtp}
        onPressCancel={() => setShowNumberModal(false)}
        title="फ़ोन नंबर चुनें"
        phoneNumbers={phone_numbers}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  innerContainer: {
    margin: 16,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color.black,
  },
  resendOtpContainer: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  resendOtpText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  phoneNumberText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
  },
  centeredContainer: {
    alignItems: "center",
  },
  rowCentered: {
    flexDirection: "row",
    justifyContent: "center",
  },
  mediumBlackText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.mediumBlacklightBlack,
  },
  resendActionText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  countdownText: {
    color: color.red,
  },
  extraInfoText: {
    padding: 16,
    paddingVertical: 10,
    color: color.mediumBlacklightBlack,
    textAlign: "center",
  },
});

export default NewOTPVerification;
